import { InjectionToken } from '@angular/core';
import { Action, ActionReducerMap, createReducer, on } from '@ngrx/store';
import { ChangeLogEntry, ChangeLogModel } from '@mona/models';
import { makeAsyncActionReducer, SucceededAction } from '@mona/store';
import { ChangeLog, ChangeLogMap, ChangeLogState } from '../../entities';
import { ChangeLogAction } from '../actions/change-log.action';

/**
 * The initial state
 */
export const initialChangeLogState: ChangeLog = {
    changesMap: null,
    sortedChanges: null,
};

/**
 * Groups changes by model
 * @param changes ChangeLogEntry[]
 */
export const groupChanges = (changes: ChangeLogEntry<ChangeLogModel>[]): ChangeLogMap => {
    return changes.reduce((changesMap, change) => {
        const modelChanges = changesMap[change.model];

        if (modelChanges) {
            modelChanges.push(change);
        } else {
            changesMap[change.model] = [change];
        }

        return changesMap;
    }, {} as ChangeLogMap);
};

/**
 * Sorts changes by created at - ASC
 * @param changes ChangeLogEntry<ChangeLogModel>[]
 */
export const sortChanges = (changes: ChangeLogEntry<ChangeLogModel>[]): ChangeLogEntry<ChangeLogModel>[] => {
    const sortedChanges = [...changes];
    sortedChanges.sort((prev, next) => {
        return new Date(prev.createdAt).getTime() - new Date(next.createdAt).getTime();
    });

    return sortedChanges;
};

/**
 * Reducer for the load changes succeeded action
 * @param state ChangeLog
 * @param action SucceededAction<ChangeLogEntry[]>
 */
export const reduceLoadChangesSucceeded = (
    state: ChangeLog,
    action: SucceededAction<ChangeLogEntry<ChangeLogModel>[]>,
): ChangeLog => {
    // returns empty map if no changes
    if (!action.payload?.length) {
        return {
            changesMap: {},
            sortedChanges: [],
        };
    }

    // sort changes
    const sortedChanges = sortChanges(action.payload);
    // convert to object map by grouping by model
    return {
        sortedChanges,
        changesMap: groupChanges(sortedChanges),
    };
};

/**
 * Appends an entry to changesMap under its model key
 * @param map
 * @param entry
 */
const appendToMap = (map: ChangeLogMap, entry: ChangeLogEntry<ChangeLogModel>): void => {
    const existing = map[entry.model];
    map[entry.model] = existing ? [...existing, entry] : [entry];
};

/**
 * Finds and replaces the isSaving entry for given model+modelId, or appends if not found
 * @param map
 * @param entry
 */
const replaceOptimisticInMap = (map: ChangeLogMap, entry: ChangeLogEntry<ChangeLogModel>): void => {
    const list = map[entry.model] || [];
    const idx = list.findIndex(
        c => c.isSaving && c.modelId === entry.modelId && c.model === entry.model && c.operation === entry.operation,
    );
    if (idx !== -1) {
        const updated = [...list];
        updated[idx] = entry;
        map[entry.model] = updated;
    } else {
        map[entry.model] = [...list, entry];
    }
};

/**
 * Finds and replaces in sortedChanges, or appends if not found
 * @param sorted
 * @param entry
 */
const replaceOptimisticInSorted = (
    sorted: ChangeLogEntry<ChangeLogModel>[],
    entry: ChangeLogEntry<ChangeLogModel>,
): ChangeLogEntry<ChangeLogModel>[] => {
    const idx = sorted.findIndex(
        c => c.isSaving && c.modelId === entry.modelId && c.model === entry.model && c.operation === entry.operation,
    );
    if (idx !== -1) {
        const result = [...sorted];
        result[idx] = entry;
        return result;
    }
    return [...sorted, entry];
};

/**
 * Reducer: optimistic add on saveChange dispatch (before API responds)
 * @param state ChangeLog
 * @param action action with change payload
 */
export const reduceSaveChangeOptimistic = (
    state: ChangeLog,
    action: { change: ChangeLogEntry<ChangeLogModel> },
): ChangeLog => {
    const entry: ChangeLogEntry<ChangeLogModel> = { ...action.change, isSaving: true, createdAt: new Date() };
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};
    appendToMap(updatedMap, entry);

    return {
        sortedChanges: [...(state.sortedChanges || []), entry],
        changesMap: updatedMap,
    };
};

/**
 * Reducer: save change succeeded — replaces optimistic entry with confirmed one
 * @param state ChangeLog
 * @param action SucceededAction with confirmed entry
 */
export const reduceSaveChangeSucceeded = (
    state: ChangeLog,
    action: SucceededAction<ChangeLogEntry<ChangeLogModel>>,
): ChangeLog => {
    const entry: ChangeLogEntry<ChangeLogModel> = { ...action.payload, isSaving: false };
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};
    replaceOptimisticInMap(updatedMap, entry);

    return {
        sortedChanges: replaceOptimisticInSorted(state.sortedChanges || [], entry),
        changesMap: updatedMap,
    };
};

/**
 * Reducer: save change failed — removes optimistic entry
 * @param state ChangeLog
 * @param action action with the failed change
 */
export const reduceSaveChangeFailed = (
    state: ChangeLog,
    action: { change: ChangeLogEntry<ChangeLogModel> },
): ChangeLog => {
    const { model, modelId } = action.change;
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};
    updatedMap[model] = (updatedMap[model] || []).filter(c => !(c.isSaving && c.modelId === modelId));

    return {
        sortedChanges: (state.sortedChanges || []).filter(
            c => !(c.isSaving && c.modelId === modelId && c.model === model),
        ),
        changesMap: updatedMap,
    };
};

/**
 * Reducer: optimistic add on saveChanges (plural) dispatch
 * @param state ChangeLog
 * @param action action with changes array
 */
export const reduceSaveChangesOptimistic = (
    state: ChangeLog,
    action: { changes: ChangeLogEntry<ChangeLogModel>[] },
): ChangeLog => {
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};
    const entries = action.changes.map(c => ({ ...c, isSaving: true, createdAt: new Date() }));
    entries.forEach(entry => appendToMap(updatedMap, entry));

    return {
        sortedChanges: [...(state.sortedChanges || []), ...entries],
        changesMap: updatedMap,
    };
};

/**
 * Reducer: save changes succeeded — replaces optimistic entries
 * @param state ChangeLog
 * @param action SucceededAction with confirmed entries
 */
export const reduceSaveChangesSucceeded = (
    state: ChangeLog,
    action: SucceededAction<ChangeLogEntry<ChangeLogModel>[]>,
): ChangeLog => {
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};
    let sortedChanges = [...(state.sortedChanges || [])];

    for (const confirmed of action.payload) {
        const entry: ChangeLogEntry<ChangeLogModel> = { ...confirmed, isSaving: false };
        replaceOptimisticInMap(updatedMap, entry);
        sortedChanges = replaceOptimisticInSorted(sortedChanges, entry);
    }

    return { sortedChanges, changesMap: updatedMap };
};

/**
 * Reducer: save changes failed — removes all optimistic entries from that batch
 * @param state ChangeLog
 * @param action action with the failed changes batch
 */
export const reduceSaveChangesFailed = (
    state: ChangeLog,
    action: { changes: ChangeLogEntry<ChangeLogModel>[] },
): ChangeLog => {
    const failedIds = new Set(action.changes.map(c => `${c.model}:${c.modelId}`));
    const updatedMap = state.changesMap ? { ...state.changesMap } : {};

    for (const change of action.changes) {
        updatedMap[change.model] = (updatedMap[change.model] || []).filter(
            c => !(c.isSaving && c.modelId === change.modelId),
        );
    }

    return {
        sortedChanges: (state.sortedChanges || []).filter(
            c => !(c.isSaving && failedIds.has(`${c.model}:${c.modelId}`)),
        ),
        changesMap: updatedMap,
    };
};

/**
 * Reducer for the persist changes succeeded action
 * @param state ChangeLog
 * @param action SucceededAction<ChangeLogEntry[]>
 */
export const reducePersistChangesSucceeded = (state: ChangeLog, action: { ids: string[] }): ChangeLog => {
    const updatedChanges = [...state.sortedChanges].filter(change => !action.ids.includes(change.id));

    return {
        sortedChanges: updatedChanges,
        changesMap: groupChanges(updatedChanges),
    };
};

/**
 * Reducer for the discard changes succeeded action
 * If ids are provided, only discards those specific changes
 * Otherwise, resets the entire change log
 */
export const reduceDiscardChangesSucceeded = (
    state: ChangeLog,
    action: SucceededAction<{ ids: string[] }>,
): ChangeLog => {
    // If ids are provided, only discard those specific changes
    if (action.payload?.ids?.length) {
        const idsSet = new Set(action.payload.ids);
        const updatedChanges = (state.sortedChanges || []).filter(change => !idsSet.has(change.modelId));

        return {
            sortedChanges: updatedChanges,
            changesMap: groupChanges(updatedChanges),
        };
    }

    // If no ids provided, reset entire change log
    return initialChangeLogState;
};

/**
 * Reducer injection token definition
 */
export const CHANGELOG_REDUCER_TOKEN = new InjectionToken<ActionReducerMap<ChangeLogState, Action>>(
    'ChangelogReducerToken',
);

/**
 * Change log reducer
 */
export const changeLogReducer = createReducer(
    initialChangeLogState,

    on(ChangeLogAction.loadChangesAction.succeededAction, reduceLoadChangesSucceeded),

    on(ChangeLogAction.saveChangeAction.action, reduceSaveChangeOptimistic),
    on(ChangeLogAction.saveChangeAction.succeededAction, reduceSaveChangeSucceeded),
    on(ChangeLogAction.revertOptimisticChange, reduceSaveChangeFailed),

    on(ChangeLogAction.saveChangesAction.action, reduceSaveChangesOptimistic),
    on(ChangeLogAction.saveChangesAction.succeededAction, reduceSaveChangesSucceeded),
    on(ChangeLogAction.revertOptimisticChanges, reduceSaveChangesFailed),

    on(ChangeLogAction.revertStaleSavingEntries, reduceSaveChangesFailed),

    on(ChangeLogAction.clearPersistedSucceededModels, reducePersistChangesSucceeded),

    on(ChangeLogAction.discardChangesAction.succeededAction, reduceDiscardChangesSucceeded),
);

/**
 * Change log reducers
 */
export const changeLogReducerMap: ActionReducerMap<ChangeLogState> = {
    changeLog: changeLogReducer,
    loadChangesAction: makeAsyncActionReducer(ChangeLogAction.loadChangesAction),
    saveChangeAction: makeAsyncActionReducer(ChangeLogAction.saveChangeAction),
    saveChangesAction: makeAsyncActionReducer(ChangeLogAction.saveChangesAction) as any /* FIXME: */,
    persistChangesAction: makeAsyncActionReducer(ChangeLogAction.persistChangesAction),
    discardChangesAction: makeAsyncActionReducer(ChangeLogAction.discardChangesAction),
};
